import { apiUrl, IS_DEMO } from '../../apiBase';
import { isPublicTutorialRoute } from '../../publicRoute';
import { isStandalone } from '../../standalone';

// LINES FOR THE SERVER'S LOG (journalctl), to read what a 3D view did after the fact, from the
// numbers rather than from a screenshot (Louis 2026-10-23). Gathered and sent once a second.
//   "cam":  what the camera did (drag steps, and frames it moved without one). The signed-in app.
//   "load": how long a scene took to load, step by step. The app, and the published page too, for
//           every visitor (Louis 2026-10-23): it is the page whose loading is being measured.
// Never from the demo, which refuses writes.
export type ServerLog = { say: (line: string) => void; flush: () => void; stop: () => void };

export function serverLog(tag: 'cam' | 'load', view?: string): ServerLog | null {
  if (IS_DEMO) return null;
  const published = isPublicTutorialRoute();
  if (published && tag !== 'load') return null;
  const route = published ? '/api/notice/public/tracelog' : '/api/notice/camlog';
  const id = view || Math.random().toString(36).slice(2, 6);
  // Room for every mesh's timing: the browser keeps 250 by default, and a page of four colourways
  // asks for more than that.
  if (tag === 'load') performance.setResourceTimingBufferSize?.(2000);
  let lines: string[] = [];
  // SENT BY FETCH, not sendBeacon: a phone's browser gives beacons a small budget for the whole
  // page (64 KB in flight), a page of 3D views overran it, and every line past it was dropped
  // without a word, the log ending on a screen the visitor had long left (Louis 2026-10-23).
  // `keepalive` only for a flush asked for as the page goes (see `flush` below): it outlives the
  // page, but shares the beacons' small budget, so the once-a-second sends do without it.
  const flush = (keepalive = false) => {
    if (!lines.length) return;
    void fetch(apiUrl(route), {
      method: 'POST',
      keepalive,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tag, view: id, lines: lines.slice(0, 200) }),
    }).catch(() => {});
    lines = [];
  };
  // EVERY QUARTER SECOND ON THE PUBLISHED PAGE, every second elsewhere: a page the phone kills sends
  // nothing more, and what it was doing in its last second is the line wanted (Louis 2026-10-26).
  const timer = setInterval(() => flush(), published ? 250 : 1000);
  return {
    // And the console, for whoever has it open.
    say: (line: string) => {
      const at = `${(performance.now() / 1000).toFixed(2)} ${line}`;
      lines.push(at);
      console.debug(`[${tag}] ${id} ${at}`);
    },
    // Now, rather than at the next second: for a line said as the page is being left.
    flush: () => flush(true),
    stop: () => { clearInterval(timer); flush(); },
  };
}

// THE PAGE'S OWN LIFE, in a page log: how it came to be, going out of sight and back, being left,
// and what went wrong that nothing caught. Returns the teardown.
//
// For a page seen loading twice in the installed app on an iPhone (Louis 2026-10-26): a second
// copy with no second request for it. "reload" or "back_forward" with no "left" from the copy
// before is the browser restarting a page it killed. On the app's pages too, so a trip back to
// one shows even when the tutorial's own "left" is lost.
export function watchPage(log: ServerLog | null): () => void {
  if (!log) return () => {};
  const where = () => `${location.pathname}${location.search}`;
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  log.say(`page opened by ${nav?.type ?? 'unknown'}, ${isStandalone() ? 'standalone' : 'in a browser tab'}, ${where()}`);
  const onError = (e: ErrorEvent) => log.say(`error ${e.message} at ${e.filename?.split('/').pop()}:${e.lineno}`);
  const onRejection = (e: PromiseRejectionEvent) => log.say(`unhandled ${String(e.reason?.message ?? e.reason)}`);
  // A phone that was put down is not a page that froze.
  const onVisibility = () => log.say(`page ${document.visibilityState}`);
  // Sent at once: a page being left gets no next second.
  const onPageHide = (e: PageTransitionEvent) => {
    log.say(`page left${e.persisted ? ', kept in memory' : ''}, ${where()}`);
    log.flush();
  };
  const onPageShow = (e: PageTransitionEvent) => { if (e.persisted) log.say(`page back from memory, ${where()}`); };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('pageshow', onPageShow);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('pageshow', onPageShow);
  };
}
